import type { TopicStatRow } from './communityStats';
import { medalTierFromScore, type MedalTier } from './medals';
import { RANK_ZONES } from './rankColors';

export type AnswerLike = { name: string; rank: number };
export type GuessLike = { name?: string; rank: number | null; isHint?: boolean; valid?: boolean };

export type RankHit = { rank: number; name: string; pct: number; label: string; count: number };

export type CommunityView = {
  realPlayers: number;
  synthPlayers: number;
  totalPlayers: number;
  blended: boolean;
  countByRank: Map<number, number>;
  totalGuesses: number;
  crowdAvgRank: number | null;
  foundCount: number;
  unguessed: number;
  centumthPct: number;
  mostGuessed: RankHit[];
  rarestFinds: RankHit[];
  topMisses: { text: string; label: string }[];
  zoneShares: { id: string; label: string; from: number; to: number; accent: string; pct: number }[];
  decadeShares: { lo: number; hi: number; pct: number; count: number }[];
};

export type FinishedGameRow = {
  score: number;
  ranks: (number | null)[];
};

export type ScoreBoard = {
  realRows: FinishedGameRow[];
  allScores: number[];
  median: number | null;
  percentile: number;
  medalMix: { tier: MedalTier; label: string; icon: string; pct: number }[];
};

export type PlayStyle = {
  ready: boolean;
  openAvg: number | null;
  lastAvg: number | null;
  hitRate: number | null;
  openObviousPct: number | null;
};

export type Verdict = { headline: string; sub: string };

const COMMUNITY_TARGET_PLAYERS = 40;
const PLAY_STYLE_MIN_REAL = 12;

export function hashStringToUint32(input: string) {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const pctLabel = (count: number, total: number) => {
  const pct = (count / Math.max(1, total)) * 100;
  if (pct > 0 && pct < 1) return '<1%';
  return `${Math.min(100, Math.round(pct))}%`;
};

const titleCaseWords = (s: string) => s.replace(/\b[a-z]/g, (c) => c.toUpperCase());

const pickWeighted = <T extends { w: number }>(items: T[], rand: () => number): T => {
  const sum = items.reduce((acc, it) => acc + it.w, 0);
  let r = rand() * sum;
  for (const it of items) {
    r -= it.w;
    if (r <= 0) return it;
  }
  return items[items.length - 1];
};

const pickOne = <T,>(arr: T[], rand: () => number): T => arr[Math.floor(rand() * arr.length)];

export function generateSyntheticScores(count: number, seedKey: string) {
  const rand = mulberry32(hashStringToUint32(seedKey));
  const scores: number[] = [];
  const weights = [0.06, 0.16, 0.24, 0.26, 0.16, 0.1, 0.02];
  const cumulative: number[] = [];
  let acc = 0;
  for (const w of weights) {
    acc += w;
    cumulative.push(acc);
  }
  const pickBin = () => {
    const r = rand();
    for (let i = 0; i < cumulative.length; i++) {
      if (r <= cumulative[i]) return i;
    }
    return cumulative.length - 1;
  };
  const tri = () => (rand() + rand()) / 2;

  for (let i = 0; i < count; i++) {
    const bin = pickBin();
    let s = 0;
    if (bin === 0) s = 0;
    if (bin === 1) s = 1 + Math.floor(tri() * 50);
    if (bin === 2) s = 51 + Math.floor(tri() * 100);
    if (bin === 3) s = 151 + Math.floor(tri() * 150);
    if (bin === 4) s = 301 + Math.floor(tri() * 100);
    if (bin === 5) s = 401 + Math.floor(tri() * 80);
    if (bin === 6) s = 481 + Math.floor(tri() * 9);
    if (s === 490 && rand() < 0.9) s = 486 + Math.floor(rand() * 4);
    scores.push(Math.max(0, Math.min(490, s)));
  }
  return scores;
}

export function buildCommunityView(
  rows: TopicStatRow[],
  allAnswers: AnswerLike[],
  topicId: string
): CommunityView | null {
  if (allAnswers.length === 0) return null;

  const realPlayers = rows.find((r) => r.kind === 'meta' && r.key === 'players')?.count ?? 0;

  const countByRank = new Map<number, number>();
  for (const r of rows) {
    if (r.kind !== 'answer') continue;
    const rank = Number(r.key);
    if (rank >= 1 && rank <= 100) countByRank.set(rank, (countByRank.get(rank) || 0) + r.count);
  }

  const synthPlayers = Math.max(0, COMMUNITY_TARGET_PLAYERS - realPlayers);
  if (synthPlayers > 0) {
    const rand = mulberry32(hashStringToUint32(`community:${topicId}`));
    const ranks = allAnswers.map((a) => a.rank);
    const weights = ranks.map((rank) => Math.pow(101 - rank, 1.4) * (0.5 + rand()));
    const cumulative: number[] = [];
    let acc = 0;
    for (const w of weights) {
      acc += w;
      cumulative.push(acc);
    }
    for (let p = 0; p < synthPlayers; p++) {
      const picked = new Set<number>();
      let guard = 0;
      while (picked.size < 5 && guard++ < 50) {
        const r = rand() * acc;
        let idx = cumulative.findIndex((c) => r <= c);
        if (idx < 0) idx = cumulative.length - 1;
        picked.add(ranks[idx]);
      }
      picked.forEach((rank) => countByRank.set(rank, (countByRank.get(rank) || 0) + 1));
    }
  }

  const totalPlayers = realPlayers + synthPlayers;
  const nameByRank = new Map(allAnswers.map((a) => [a.rank, a.name] as const));
  const entries = Array.from(countByRank.entries()).filter(([rank]) => nameByRank.has(rank));

  const toHit = ([rank, count]: [number, number]): RankHit => ({
    rank,
    name: nameByRank.get(rank)!,
    count,
    pct: (count / Math.max(1, totalPlayers)) * 100,
    label: pctLabel(count, totalPlayers),
  });

  const mostGuessed = [...entries]
    .sort((a, b) => b[1] - a[1] || a[0] - b[0])
    .slice(0, 5)
    .map(toHit);

  const rarestFinds = [...entries]
    .filter(([, count]) => count > 0)
    .sort((a, b) => a[1] - b[1] || b[0] - a[0])
    .slice(0, 3)
    .map(toHit);

  const topMisses =
    realPlayers >= 25
      ? rows
          .filter((r) => r.kind === 'miss' && r.count >= 3)
          .sort((a, b) => b.count - a.count)
          .slice(0, 8)
          .map((r) => ({ text: titleCaseWords(r.key), label: pctLabel(r.count, realPlayers) }))
      : [];

  let totalGuesses = 0;
  let weighted = 0;
  countByRank.forEach((count, rank) => {
    totalGuesses += count;
    weighted += count * rank;
  });

  let foundCount = 0;
  for (let rank = 1; rank <= 100; rank++) {
    if ((countByRank.get(rank) ?? 0) > 0) foundCount += 1;
  }

  const zoneShares = RANK_ZONES.map((z) => {
    let c = 0;
    countByRank.forEach((count, rank) => {
      if (rank >= z.from && rank <= z.to) c += count;
    });
    return {
      id: z.id,
      label: z.label,
      from: z.from,
      to: z.to,
      accent: z.accent,
      pct: totalGuesses > 0 ? Math.round((c / totalGuesses) * 100) : 0,
    };
  });

  const decadeShares = Array.from({ length: 10 }, (_, i) => {
    const lo = i * 10 + 1;
    const hi = (i + 1) * 10;
    let c = 0;
    countByRank.forEach((count, rank) => {
      if (rank >= lo && rank <= hi) c += count;
    });
    return {
      lo,
      hi,
      count: c,
      pct: totalGuesses > 0 ? Math.round((c / totalGuesses) * 100) : 0,
    };
  });

  return {
    realPlayers,
    synthPlayers,
    totalPlayers,
    blended: synthPlayers > 0,
    countByRank,
    totalGuesses,
    crowdAvgRank: totalGuesses > 0 ? Math.round(weighted / totalGuesses) : null,
    foundCount,
    unguessed: 100 - foundCount,
    centumthPct: (countByRank.get(100) ?? 0) / Math.max(1, totalPlayers) * 100,
    mostGuessed,
    rarestFinds,
    topMisses,
    zoneShares,
    decadeShares,
  };
}

export function buildScoreBoard(
  realRows: FinishedGameRow[],
  topicId: string,
  yourScore: number
): ScoreBoard {
  const realScores = realRows.map((r) => r.score);
  const missing = Math.max(0, 80 - realScores.length);
  const syntheticScores = generateSyntheticScores(missing, `topic:${topicId}`);
  const allScores = [...realScores, ...syntheticScores];

  let percentile = 100;
  if (allScores.length > 1) {
    const below = allScores.filter((s) => s < yourScore).length;
    percentile = Math.min(100, Math.max(0, Math.round((below / (allScores.length - 1)) * 100)));
  }

  const sorted = [...allScores].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median =
    sorted.length === 0
      ? null
      : sorted.length % 2
        ? sorted[mid]
        : Math.round((sorted[mid - 1] + sorted[mid]) / 2);

  const tiers: { tier: MedalTier; label: string; icon: string }[] = [
    { tier: 'none', label: 'No medal', icon: '—' },
    { tier: 'bronze', label: 'Bronze', icon: '🥉' },
    { tier: 'silver', label: 'Silver', icon: '🥈' },
    { tier: 'gold', label: 'Gold', icon: '🥇' },
    { tier: 'platinum', label: 'Platinum', icon: '💎' },
  ];
  const medalMix = tiers.map((t) => {
    const n = allScores.filter((s) => medalTierFromScore(s) === t.tier).length;
    return { ...t, pct: allScores.length ? Math.round((n / allScores.length) * 100) : 0 };
  });

  return { realRows, allScores, median, percentile, medalMix };
}

export function buildPlayStyle(realRows: FinishedGameRow[]): PlayStyle {
  if (realRows.length < PLAY_STYLE_MIN_REAL) {
    return { ready: false, openAvg: null, lastAvg: null, hitRate: null, openObviousPct: null };
  }

  const first: number[] = [];
  const last: number[] = [];
  let hits = 0;
  let slots = 0;
  let openObvious = 0;

  for (const row of realRows) {
    const open = row.ranks[0];
    if (typeof open === 'number' && open > 0) {
      first.push(open);
      if (open <= 25) openObvious += 1;
    }
    const close = row.ranks[4];
    if (typeof close === 'number' && close > 0) last.push(close);
    for (const r of row.ranks) {
      slots += 1;
      if (typeof r === 'number' && r > 0) hits += 1;
    }
  }

  const avg = (xs: number[]) => (xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null);

  return {
    ready: true,
    openAvg: avg(first),
    lastAvg: avg(last),
    hitRate: slots ? Math.round((hits / slots) * 10) / 10 : null,
    openObviousPct: first.length ? Math.round((openObvious / first.length) * 100) : null,
  };
}

export function officialRanks(guesses: GuessLike[]): number[] {
  return guesses
    .slice(0, 5)
    .filter((g) => g.valid !== false && !g.isHint && g.rank)
    .map((g) => Number(g.rank));
}

export function displayAnswerName(
  rank: number,
  name: string,
  yourRanks: Set<number>,
  listRevealed: boolean
) {
  if (listRevealed || yourRanks.has(rank)) return name;
  return '???';
}

export function buildInsights(view: CommunityView, yourRanks: Set<number>): { key: string; text: string }[] {
  if (view.totalGuesses === 0) return [];
  const items: { key: string; text: string }[] = [];

  const top10 = view.decadeShares[0]?.pct ?? 0;
  items.push({
    key: 'top10',
    text:
      top10 >= 25
        ? `${top10}% of all guesses landed in the top 10 — people can’t resist the obvious.`
        : `Only ${top10}% of guesses went for the top 10 — the crowd dug deeper than usual.`,
  });

  const deep = view.decadeShares[9]?.pct ?? 0;
  items.push({
    key: 'deep',
    text:
      deep < 10
        ? `Just ${deep}% of guesses made it past #90 — the big points stayed hidden.`
        : `A bold ${deep}% of guesses went hunting past #90.`,
  });

  const busiest = view.decadeShares.reduce((a, b) => (b.count > a.count ? b : a));
  if (busiest.lo !== 1) {
    items.push({
      key: 'busiest',
      text: `#${busiest.lo}–${busiest.hi} was today’s comfort zone, drawing ${busiest.pct}% of all guesses.`,
    });
  }
  const quietest = view.decadeShares.reduce((a, b) => (b.count < a.count ? b : a));
  items.push({
    key: 'quietest',
    text: `#${quietest.lo}–${quietest.hi} was a ghost town — only ${quietest.pct}% of guesses landed there.`,
  });

  const secondHalf = view.zoneShares.filter((z) => z.from >= 51).reduce((a, z) => a + z.pct, 0);
  items.push({
    key: 'half',
    text:
      secondHalf < 35
        ? `The crowd played it safe — only ${secondHalf}% of guesses crossed #50.`
        : `${secondHalf}% of guesses ventured into the second half (#51+).`,
  });

  if (view.unguessed >= 5) {
    items.push({
      key: 'unguessed',
      text: `${view.unguessed} of the 100 answers weren’t guessed by a single player today.`,
    });
  }

  if (yourRanks.size > 0 && view.crowdAvgRank) {
    let yourSum = 0;
    yourRanks.forEach((r) => {
      yourSum += r;
    });
    const yourAvg = Math.round(yourSum / yourRanks.size);
    const crowdAvg = view.crowdAvgRank;
    items.push({
      key: 'you',
      text:
        yourAvg >= crowdAvg + 8
          ? `Your picks ran deeper than the crowd’s — you averaged #${yourAvg} vs their #${crowdAvg}.`
          : yourAvg <= crowdAvg - 8
            ? `You played it safer than the crowd — you averaged #${yourAvg} vs their #${crowdAvg}.`
            : `You guessed right in step with the crowd — you averaged #${yourAvg} vs their #${crowdAvg}.`,
    });
  }

  const c100 = view.countByRank.get(100) ?? 0;
  if (c100 > 0) {
    items.push({
      key: 'centumth',
      text: `${pctLabel(c100, view.totalPlayers)} of players found the Centumth.`,
    });
  }

  return items;
}

const RAREST_JOKES = [
  'Today’s ghost. Blink and you’d miss it — most people did.',
  'Practically a rumor. The list’s introvert.',
  'If this were a concert, it was the song nobody stayed for.',
  'A needle. A haystack. You get the idea.',
  'So rare it might have been a typo. It wasn’t.',
  'The answer that whispered. The room shouted over it.',
];

const MISS_HEADERS = [
  'Traps the room walked into',
  'Confident. Wrong. Iconic.',
  'The “wait, that’s not on here?” club',
];

const MISS_TAGS = [
  'walked right into it',
  'said it with their whole chest',
  'absolutely sure. absolutely not.',
  'the classic fake-out',
  'close enough to hurt',
];

export function rarestJoke(seedKey: string) {
  const rand = mulberry32(hashStringToUint32(`rarest:${seedKey}`));
  return pickOne(RAREST_JOKES, rand);
}

export function missFlavor(seedKey: string) {
  const rand = mulberry32(hashStringToUint32(`misses:${seedKey}`));
  return {
    header: pickOne(MISS_HEADERS, rand),
    tags: MISS_TAGS,
  };
}

export function pickVerdict(args: {
  topicId: string;
  userId: string;
  score: number;
  guesses: GuessLike[];
  yourRanks: number[];
  community: CommunityView;
  median: number | null;
  percentile: number | null;
}): Verdict {
  const { topicId, userId, score, guesses, yourRanks, community, median, percentile } = args;
  const rand = mulberry32(hashStringToUint32(`verdict:${topicId}:${userId}:${score}`));

  const official = guesses.slice(0, 5);
  const hits = yourRanks.length;
  const misses = official.filter((g) => !g.isHint && (g.valid === false || !g.rank)).length;
  const hitCentumth = yourRanks.includes(100);
  const yourAvg = hits ? Math.round(yourRanks.reduce((a, b) => a + b, 0) / hits) : null;
  const crowdAvg = community.crowdAvgRank;
  const top3 = new Set(community.mostGuessed.slice(0, 3).map((m) => m.rank));
  const overlap = yourRanks.filter((r) => top3.has(r)).length;
  const foundRarest = community.rarestFinds[0] ? yourRanks.includes(community.rarestFinds[0].rank) : false;
  const rareOwn = yourRanks.filter((r) => {
    const c = community.countByRank.get(r) ?? 0;
    return c > 0 && c / community.totalPlayers < 0.08;
  }).length;
  const allDeep = hits >= 3 && yourRanks.every((r) => r >= 50);
  const allObvious = hits >= 3 && yourRanks.every((r) => r <= 25);
  const mixed = yourRanks.some((r) => r <= 15) && yourRanks.some((r) => r >= 80);

  type Cand = { w: number; headlines: string[]; subs: string[] };
  const pool: Cand[] = [];

  if (hitCentumth) {
    pool.push({
      w: 10,
      headlines: [
        'You found the Centumth.',
        'THE one. You actually got #100.',
        'The last slot on the list? Yours.',
        'Most people never even look that far. You did.',
      ],
      subs: [
        'That’s the rarest flex in the game.',
        'The room will be talking about this one.',
        'Deep cut of deep cuts.',
      ],
    });
  }

  if (score === 490) {
    pool.push({
      w: 10,
      headlines: ['Perfect. That’s a Platinum.', 'You cleaned the list out.', '490. That’s the ceiling.'],
      subs: ['There is nowhere left to go.', 'The rest of us can go home.'],
    });
  }

  if (hits === 5) {
    pool.push({
      w: 7,
      headlines: [
        'Five for five. The list never stood a chance.',
        'A clean sweep. Not a single miss.',
        'Every guess landed. That’s rude.',
        'You treated the Top 100 like a shopping list.',
      ],
      subs: ['No wasted lives. Just points.', 'The room is taking notes.'],
    });
  }

  if (hits === 0) {
    pool.push({
      w: 8,
      headlines: [
        'The list said no. Repeatedly.',
        'Today was a character-building exercise.',
        'Five swings. Five ghosts.',
        'The Top 100 would like a word. Several, actually. None of them yours.',
      ],
      subs: [
        'Arcade Mode is right there if you want revenge.',
        'Happens to the best of us. Allegedly.',
      ],
    });
  }

  if (hits === 1) {
    pool.push({
      w: 6,
      headlines: [
        'One survived. Barely a rescue mission.',
        'A single light in the fog.',
        'Four misses and a miracle.',
      ],
      subs: ['One point-scoring guess is still a point-scoring guess.', 'That’s a story, at least.'],
    });
  }

  if (foundRarest) {
    pool.push({
      w: 8,
      headlines: [
        'You found today’s ghost.',
        'The rarest find of the day? That was you.',
        'You picked the answer nobody else remembered.',
      ],
      subs: [
        `Only ${community.rarestFinds[0]?.label} of players got there.`,
        'The room walked past it. You didn’t.',
      ],
    });
  }

  if (rareOwn >= 2) {
    pool.push({
      w: 6,
      headlines: [
        'You collect things other people miss.',
        'Two rare finds. That’s a taste.',
        'You were shopping in the back of the store.',
      ],
      subs: ['The popular answers can wait.', 'Crate-digging energy.'],
    });
  }

  if (yourAvg != null && crowdAvg != null && yourAvg >= crowdAvg + 16) {
    pool.push({
      w: 7,
      headlines: [
        'You went looking where nobody else did.',
        'The room played the hits. You went crate-digging.',
        'You hunted. They browsed.',
        'Deep water. You jumped in anyway.',
      ],
      subs: [
        `You averaged #${yourAvg}. The room sat at #${crowdAvg}.`,
        'That’s a different sport from what everyone else played.',
      ],
    });
  } else if (yourAvg != null && crowdAvg != null && yourAvg >= crowdAvg + 8) {
    pool.push({
      w: 5,
      headlines: [
        'A little deeper than the pack.',
        'You drifted past the obvious.',
        'Slightly more adventurous than the room.',
      ],
      subs: [`#${yourAvg} average vs their #${crowdAvg}.`, 'Not safe. Not reckless. Just better.'],
    });
  } else if (yourAvg != null && crowdAvg != null && yourAvg <= crowdAvg - 16) {
    pool.push({
      w: 7,
      headlines: [
        'You stayed in the shallow end.',
        'Household names. Household points.',
        'You played the crowd-pleasers.',
        'Safe picks. Safe points. No judgment.',
      ],
      subs: [
        `You averaged #${yourAvg}. The room wandered to #${crowdAvg}.`,
        'The obvious answers exist for a reason.',
      ],
    });
  } else if (yourAvg != null && crowdAvg != null && yourAvg <= crowdAvg - 8) {
    pool.push({
      w: 5,
      headlines: [
        'You played it safer than the crowd.',
        'A bit more obvious than the room.',
        'You kept to familiar ground.',
      ],
      subs: [`#${yourAvg} average vs their #${crowdAvg}.`, 'Nothing wrong with a sure thing.'],
    });
  } else if (yourAvg != null && crowdAvg != null) {
    pool.push({
      w: 3,
      headlines: [
        'Same wavelength as the room.',
        'You and the crowd had the same idea.',
        'Locked in step with everyone else.',
        'Great minds. Identical shopping lists.',
      ],
      subs: [
        `You both hovered around #${yourAvg}.`,
        'Not a copy. Just… synchronized.',
      ],
    });
  }

  if (overlap >= 3 && hits >= 3) {
    pool.push({
      w: 6,
      headlines: [
        'You and everyone else had the same grocery list.',
        'Today’s consensus? That was your board.',
        'The hive mind called. You picked up.',
      ],
      subs: ['Three of the room’s favorites were yours too.', 'Hard to be unique when the hits slap.'],
    });
  }

  if (overlap === 0 && hits >= 3) {
    pool.push({
      w: 7,
      headlines: [
        'Not a single crowd favorite. Respect.',
        'You refused to guess what everyone else guessed.',
        'Off the beaten path. On purpose, we assume.',
      ],
      subs: ['Zero overlap with today’s top three.', 'The opposite of a hive mind.'],
    });
  }

  if (allDeep) {
    pool.push({
      w: 6,
      headlines: [
        'No obvious answers. Just vibes and courage.',
        'Everything you found lived past #50.',
        'You skipped the easy ones on principle.',
      ],
      subs: ['That’s a strategy. A spicy one.', 'Points live back there. You knew.'],
    });
  }

  if (allObvious) {
    pool.push({
      w: 5,
      headlines: [
        'You went for the household names.',
        'If it’s famous, you typed it.',
        'The first page of the list, basically.',
      ],
      subs: ['Classic. Popular. Very findable.', 'The deep cuts will still be there tomorrow.'],
    });
  }

  if (mixed) {
    pool.push({
      w: 5,
      headlines: [
        'One foot in the obvious, one in the deep end.',
        'A greatest hit and a deep cut. Balanced.',
        'You covered the whole spectrum. Almost showing off.',
      ],
      subs: ['Range. That’s the word.', 'From household name to who-even-is-that.'],
    });
  }

  if (score >= 300 && score < 490) {
    pool.push({
      w: 4,
      headlines: [
        'Gold territory. The room noticed.',
        'That’s a serious score.',
        'You cooked. Quietly, even.',
      ],
      subs: [
        percentile != null ? `Better than ${percentile}% of players.` : 'Gold is Gold.',
        median != null ? `Crowd median was ${median}. You: ${score}.` : 'Heavy pockets.',
      ],
    });
  }

  if (score < 50 && hits <= 2) {
    pool.push({
      w: 5,
      headlines: [
        'A humble showing. The list won this round.',
        'Not every day is a medal day.',
        'The Top 100 kept its secrets.',
      ],
      subs: ['Come back meaner tomorrow.', 'Arcade Mode doesn’t judge.'],
    });
  }

  if (median != null && score >= median + 90) {
    pool.push({
      w: 5,
      headlines: [
        'You lapped the median.',
        'The average player did not have this kind of day.',
        'Above the waterline. Way above.',
      ],
      subs: [`Median ${median}. You ${score}.`, 'That’s a gap.'],
    });
  }

  if (misses >= 3 && hits >= 1) {
    pool.push({
      w: 3,
      headlines: [
        'A messy, honorable run.',
        'Hits and misses. Mostly the second one.',
        'You left some lives on the field.',
      ],
      subs: ['The ones that landed still count.', 'Chaos is a valid strategy.'],
    });
  }

  pool.push({
    w: 1,
    headlines: [
      'Another day, another Top 100.',
      'That’s a wrap on today’s list.',
      'The room has spoken. So have you.',
      'Five guesses later…',
    ],
    subs: [
      'Here’s how you stacked up.',
      'Let’s see what everyone else was thinking.',
      'The chart below is the rest of the story.',
    ],
  });

  const chosen = pickWeighted(pool, rand);
  return {
    headline: pickOne(chosen.headlines, rand),
    sub: pickOne(chosen.subs, rand),
  };
}
